import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Button from '../../atoms/Button/Button';
import styles from './Dropdown.css';
import { classnames } from '../../../helpers/classnames';
import ConfidenceIcon from '../../atoms/ConfidenceIcon/ConfidenceIcon';
import useTranslate from '../../../hooks/useTranslate';

function Dropdown({ className, options, onSelect, onOpen, ...props }) {
  const [open, setOpen] = useState(false);
  const [buttonEl, setButtonEl] = useState(null);
  const [scrollOffset, setScrollOffset] = useState(0);
  const i18n = useTranslate();
  // Callback optimisé pour le click du bouton principal
  const handleButtonClick = useCallback(() => {
    setOpen(true);
    onOpen && onOpen();
  }, [onOpen]);

  // Callback optimisé pour éviter les fonctions inline dans le map des options
  const handleOptionClick = useCallback((value) => {
    return () => onSelect(value);
  }, [onSelect]);

  // Recompute menu style when ref changes
  const menuStyle = useMemo(() => {
    if (!buttonEl) {
      return {};
    }
    const rect = buttonEl.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
    };
  }, [buttonEl, open, scrollOffset]);

  useEffect(() => {
    const formRoot = document.getElementById('form_root');
    if (formRoot) {
      setScrollOffset(formRoot.scrollTop);
    }
  }, [open]);

  // When the menu is open, clicking anywhere closes it
  useEffect(() => {
    if (!open) return;
    const handleClickAnywhere = () => setOpen(false);
    const formRoot = document.getElementById('form_root');
    const handleScroll = () => setScrollOffset(formRoot.scrollTop);
    const timer = setTimeout(() => document.addEventListener('click', handleClickAnywhere), 0);
    formRoot && formRoot.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickAnywhere);
      formRoot && formRoot.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  return (
    <>
      <Button
        {...props}
        className={classnames([styles.Dropdown__button, className])}
        ref={setButtonEl}
        type="button"
        onClick={handleButtonClick}
      />
      {/* Use a portal to avoid messing up with overflow: hidden stuff */}
      {open &&
        createPortal(
          <div className={styles.Dropdown__menu} style={menuStyle}>
            {options.length ? (
              options.map(({ label, value, confidence }, i) => (
                <button
                  key={i}
                  type="button"
                  className={styles.Dropdown__option}
                  onClick={handleOptionClick(value)}
                >
                  {confidence ? <ConfidenceIcon confidence={confidence} /> : null}
                  {label}
                </button>
              ))
            ) : (
              <div className={styles.Dropdown__empty}>{i18n('Dropdown.empty')}</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

Dropdown.propTypes = {
  className: PropTypes.string,
  options: PropTypes.array,
  onSelect: PropTypes.func,
  onOpen: PropTypes.func,
};

export default Dropdown;
